// websocket/move-trigger-truth.ts — Janus Session A step 11 (2026-09-30): the page half of a move's TRIGGER TAIL (final_creature_move.php
// L661–784): the mover's own triggers_on_move and the enters-reach triggers of each watcher whose reach it entered. A Force Move names them
// (janus-engine/movement/force-move.ts `triggers`, `allowMoveTriggers`).
//   PLAN   (synchronous, BEFORE the move commits): the mover's and every entered watcher's full compile, and every definition their trigger
//          carriers read, must already be held — a miss warms them and hands the move back; then the tail (resolveAbilityUse's `moveTriggers`
//          mode: the janus_trigger twin through the in-pipeline executor — pre-draw preview, extreme dry runs, the REAL draw) on the mover
//          standing at its landing. A tail the page cannot resolve hands the whole move back before anything is saved.
//   COMMIT (after the move is accepted — movement-truth.ts queueAfterAccepted, which holds the mover until it settles): the verdict through the
//          SHARED persistence, the ability commit's `move_trigger` block, under an operation id derived from (mover, landing revision).
import { PC_DYING_STATUS_IDS, resolveAbilityUse, type AbilityUseVerdict } from '../janus-engine/actions/ability-use.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import type { RollSource } from '../janus-engine/actions/roll-source.js';
import { abilityTruthReaders, commitZoneEntryVerdict } from './ability-truth.js';
import { actorEncounterFor } from './movement-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { commitOwed } from './owed-commit.js';
import type { TriggerConsent } from '../janus-engine/ability/trigger-consent.js';
import { offerFortunate } from './trigger-consent.js';

type Row = Record<string, unknown>;
type Resolved = Extract<AbilityUseVerdict, { kind: 'resolved' }>;
const row = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const TRIGGERS = ['triggers_on_move', 'triggers_when_target_enters_reach'];
const CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell', 'innate_spell_per_long_rest',
  'spells_known', 'opportunity_attack_ability'];
/** What a dispatched carrier reads beyond itself (ability-truth.ts hydrates the same keys for a cast). */
const REFERENCE_KEYS = ['status_effect_applied_to_caster_after_cast', 'status_effect_applied_to_target_after_cast', 'status_effect_applied_to_caster_on_hit',
  'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_target_after_contesting_roll_failed', 'skill_roll_contester', 'skill_roll_contestee',
  'damage_type', 'passive_ability'];

function members(encounter: Row): Map<string, Row> {
  const out = new Map<string, Row>();
  for (const m of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    const id = upper(row(row(m)?.encounter_creatures)?.encounter_creature_id);
    if (UUID.test(id)) out.set(id, m as Row);
  }
  return out;
}
const complete = (m: Row | undefined): boolean => !!m && !!row(row(m.computed)?.creature) && !!row(m.item_layer_types);
const entryId = (entry: unknown): string => upper(typeof entry === 'string' ? entry.split('|')[0] : row(row(entry)?.items)?.item_id ?? row(entry)?.item_id ?? row(entry)?.ability_id);
const layerEntries = (item: Row | null, key: string): unknown[] => {
  const v = row(item?.item_layer_types)?.[key];
  return v === undefined || v === null ? [] : Array.isArray(v) ? v : [v];
};
/** Every carrier id a participant could dispatch from (its buckets, its compiled abilities, its statuses). */
function carrierIds(member: Row): string[] {
  const ids = new Set<string>();
  const compiled = row(row(row(member.computed)?.creature)?.abilities);
  for (const carrier of CARRIERS) {
    for (const entry of [...layerEntries(member, carrier), ...(Array.isArray(member[carrier]) ? member[carrier] as unknown[] : []),
      ...(Array.isArray(compiled?.[carrier]) ? compiled![carrier] as unknown[] : [])]) {
      const id = entryId(entry);
      if (UUID.test(id)) ids.add(id);
    }
  }
  const statuses = row(member.statuses)?.target;
  for (const s of Array.isArray(statuses) ? statuses : []) { const id = upper(row(s)?.status_item_id); if (UUID.test(id)) ids.add(id); }
  return [...ids];
}
export type MoveTriggerPlan =
  | { kind: 'planned'; encounterId: string; moverId: string; entered: string[]; verdict: Resolved; abilityId: string; originals: ReadonlyMap<string, Row> }
  | { kind: 'none' }
  | { kind: 'handback'; reason: string };

/** Resolve the trigger tail of `moverId`'s move to `landing` on the rows this page holds. `roll` = the real draw (default). */
export function planMoveTriggers(encounterId: string, moverId: string, triggers: { onMove: boolean; entered: string[] },
  landing: { x: number; y: number; z: number }, roll: RollSource = browserRollSource(),
  /** CROSS-LANE SEAM (lane A11 → MV3 / MV4; OWNER RULING 2026-10-02): a PRE-ANSWERED consent reader. An enters-reach REACTION of a player's
   *  watcher (Backblast Counter, Bucking Kick) then fires only on its controller's yes; unanswered on the dry runs → hand-back
   *  `move_triggers:preflight:trigger-consent` BEFORE any real die (ask — planWithConsent in websocket/trigger-consent.ts — and plan again).
   *  Absent = PHP's auto-fire, as before. */
  consent?: TriggerConsent): MoveTriggerPlan {
  const encounter = actorEncounterFor(upper(encounterId)) as Row | null;
  if (!encounter) return { kind: 'handback', reason: 'move_triggers:encounter-not-held' };
  const all = members(encounter);
  const mover = all.get(upper(moverId));
  const entered = triggers.entered.map(upper);
  const participants = [upper(moverId), ...entered];
  const readsNow = abilityTruthReaders((mover ?? {}) as Row, encounter, new Set(participants.filter(id => complete(all.get(id)))));
  const definition = (id: string): Row | null => readsNow.definition(id) as Row | null;
  if (participants.some(id => !complete(all.get(id)))) { warmFor(encounter, participants, definition); return { kind: 'handback', reason: 'move_triggers:participant-compile-not-held' }; }
  // the tail resolves with the mover standing at its landing (the move lands it there first)
  const at = { ...mover!, position: { ...(row(mover!.position) ?? {}), x_pos: landing.x, y_pos: landing.y, z_pos: landing.z },
    encounter_creatures: { ...(row(mover!.encounter_creatures) ?? {}), x_pos: landing.x, y_pos: landing.y, z_pos: landing.z } };
  const view = new Map(all); view.set(upper(moverId), at);
  const viewEncounter = { ...encounter, encounter_creatures_arr: [...view.values()] };
  const read = { ...abilityTruthReaders(at, viewEncounter, new Set(participants)), ...(consent ? { triggerConsent: consent } : {}) };
  // a damaging tail can drop a PC to Dying at an INTERMEDIATE face no extreme dry run reaches: apply_player_dying's cascade (Crippled, Dying,
  // Maimed) must be held before any die (Codex step-11 review P1; the fall and the ordinary cast hydrate the same trio)
  const dying = [...PC_DYING_STATUS_IDS].filter(id => !read.definition(id));
  if (dying.length) { for (const id of dying) void fetchAndCacheObject(id).catch(() => null); return { kind: 'handback', reason: `move_triggers:definition-not-held:${dying[0]}` }; }
  const verdict = resolveAbilityUse({ encounterId: upper(encounterId), casterId: upper(moverId), targetIds: [upper(moverId)], abilityId: '',
    moveTriggers: { onMove: triggers.onMove, entered } }, viewEncounter, view, read, roll);
  if (verdict.kind === 'handback') {
    if (/definition-not-held|not-held|unavailable/.test(verdict.reason)) warmFor(encounter, participants, definition);
    return { kind: 'handback', reason: `move_triggers:${verdict.reason}` };
  }
  if (verdict.kind !== 'resolved') return { kind: 'handback', reason: 'move_triggers:refused' };
  const changed = verdict.rows.length || Object.keys(verdict.ec).length || verdict.statusesAdded.length || verdict.statusesRemoved.length
    || verdict.charges.length || verdict.playerCharactersChanged.length || verdict.statusStacks.length || verdict.deathTail.length || verdict.concentrationBreaks.length;
  if (!changed) return { kind: 'none' }; // every carrier gated silent — nothing to save
  const abilityId = upper(verdict.rows[0]?.roll_item_id);
  if (!UUID.test(abilityId)) return { kind: 'handback', reason: 'move_triggers:verdict-shape' };
  return { kind: 'planned', encounterId: upper(encounterId), moverId: upper(moverId), entered, verdict, abilityId, originals: view };
}
/** Warm what the next attempt needs: each participant's full compile, its trigger carriers, and what those carriers reference. */
function warmFor(encounter: Row, participants: string[], definition: (id: string) => Row | null): void {
  const all = members(encounter);
  const hydrate = (window as unknown as { fetchAndCacheCaster?: (id: string, opts: { skipEncounterSeed: true }) => Promise<void> }).fetchAndCacheCaster;
  let budget = 48;
  const fetchRefs = (def: Row | null): void => {
    if (!def || !TRIGGERS.some(t => row(def.item_layer_types)?.[t] != null)) return;
    const refs = new Set<string>();
    for (const key of REFERENCE_KEYS) for (const e of layerEntries(def, key)) { const id = entryId(e); if (UUID.test(id)) refs.add(id); }
    if (layerEntries(def, 'damage_type').length) for (const id of PC_DYING_STATUS_IDS) refs.add(id);
    for (const id of refs) if (!definition(id) && budget-- > 0) void fetchAndCacheObject(id).catch(() => null);
  };
  for (const id of participants) {
    void hydrate?.(id, { skipEncounterSeed: true })?.catch?.(() => null);
    const member = all.get(id);
    for (const carrier of member ? carrierIds(member) : []) {
      const held = definition(carrier);
      if (held) fetchRefs(held);
      else if (budget-- > 0) void fetchAndCacheObject(carrier).then(def => fetchRefs(row(def))).catch(() => null);
    }
  }
}

/** Commit a planned tail once the landing is saved at `rev`. The mover's next move waits on the returned promise (queueAfterAccepted). */
export async function commitMoveTriggers(plan: Extract<MoveTriggerPlan, { kind: 'planned' }>, rev: number): Promise<void> {
  try {
    const result = await commitOwed('The move’s triggered effects', () => commitZoneEntryVerdict({ encounterId: plan.encounterId, actorId: plan.moverId,
      abilityId: plan.abilityId, zoneStatusId: '', verdict: plan.verdict as never, before: plan.originals, expectedRevs: new Map([[plan.moverId, rev]]),
      moveTrigger: { entered: plan.entered } }), { moverId: plan.moverId, entered: plan.entered });
    if (result.outcome !== 'served') console.warn('[MOVE-TRIGGER-TRUTH] the owed trigger tail was not saved', { moverId: plan.moverId, outcome: result.outcome });
    else if (plan.verdict.fortunateOffers?.length) offerFortunate(plan.encounterId, plan.moverId, plan.verdict.fortunateOffers); // owner 2026-10-03
    window.dispatchEvent(new CustomEvent('ft:move-trigger-truth', { detail: { encounterId: plan.encounterId, moverId: plan.moverId, entered: plan.entered, outcome: result.outcome } }));
  } catch (error) { console.warn('[MOVE-TRIGGER-TRUTH] commit failed', error); }
}
Object.assign(window, { ftPlanMoveTriggers: planMoveTriggers });
